/* DayDoodlePad — compact doodle canvas embedded inside DayDetailModal.
 * Lets the user draw a small doodle/sketch on the expanded cell day,
 * save it, and see all earlier doodles for that same date as thumbnails.
 */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Paintbrush, Eraser, Undo2, Save, Trash2, Sparkle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Amethyst-leaning palette (matches the lavender calendar theme)
const COLORS = [
  '#4A2D8E', '#7C5FCC', '#B898FF', '#E0D1FF',
  '#F5F0FF', '#FF6B9D', '#A0E8D5', '#FFD166',
];
const BRUSH_SIZES = [2, 4, 7, 12];

export default function DayDoodlePad({ date }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [color, setColor] = useState('#4A2D8E');
  const [brushSize, setBrushSize] = useState(4);
  const [tool, setTool] = useState('pen'); // pen | eraser
  const [drawing, setDrawing] = useState(false);
  const historyRef = useRef([]);
  const [saved, setSaved] = useState([]);
  const [busy, setBusy] = useState(false);
  const lastPt = useRef(null);

  const drawDotGrid = (ctx, w, h) => {
    ctx.fillStyle = '#FFFBFF';
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = '#E0D1FF';
    const sp = 16;
    for (let x = sp; x < w; x += sp) {
      for (let y = sp; y < h; y += sp) {
        ctx.beginPath();
        ctx.arc(x, y, 0.9, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  };

  const initCanvas = useCallback(() => {
    const c = canvasRef.current;
    const wrap = containerRef.current;
    if (!c || !wrap) return;
    const rect = wrap.getBoundingClientRect();
    c.width = Math.max(280, Math.floor(rect.width));
    c.height = 220;
    const ctx = c.getContext('2d');
    drawDotGrid(ctx, c.width, c.height);
    historyRef.current = [c.toDataURL()];
  }, []);

  useEffect(() => { initCanvas(); }, [initCanvas]);

  const fetchSaved = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bujo/doodles`, { params: { date }, withCredentials: true });
      setSaved(Array.isArray(r.data) ? r.data : []);
    } catch {/**/}
  }, [date]);
  useEffect(() => { fetchSaved(); }, [fetchSaved]);

  const pushHistory = () => {
    const c = canvasRef.current;
    if (!c) return;
    historyRef.current = [...historyRef.current.slice(-15), c.toDataURL()];
  };

  const getPt = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    const cx = t ? t.clientX : e.clientX;
    const cy = t ? t.clientY : e.clientY;
    return { x: ((cx - r.left) * c.width) / r.width, y: ((cy - r.top) * c.height) / r.height };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    lastPt.current = getPt(e);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    const p = getPt(e);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = tool === 'eraser' ? '#FFFBFF' : color;
    ctx.lineWidth = tool === 'eraser' ? brushSize * 2.5 : brushSize;
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPt.current = p;
  };
  const end = () => {
    if (drawing) {
      pushHistory();
      setDrawing(false);
    }
  };

  const undo = () => {
    const hist = historyRef.current;
    if (hist.length < 2) return;
    const next = hist.slice(0, -1);
    historyRef.current = next;
    const img = new Image();
    img.onload = () => {
      const c = canvasRef.current;
      const ctx = c.getContext('2d');
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = next[next.length - 1];
  };

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    drawDotGrid(ctx, c.width, c.height);
    historyRef.current = [c.toDataURL()];
  };

  const save = async () => {
    const c = canvasRef.current;
    if (!c) return;
    setBusy(true);
    try {
      const dataUrl = c.toDataURL('image/png');
      await axios.post(`${API}/bujo/doodles`, {
        title: `Doodle ${date}`,
        data_url: dataUrl,
        date,
      }, { withCredentials: true });
      toast.success('🎨 Doodle sparad 💜');
      await fetchSaved();
      clear();
    } catch (e) {
      toast.error('Kunde inte spara doodle');
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Ta bort denna doodle?')) return;
    try {
      await axios.delete(`${API}/bujo/doodles/${id}`, { withCredentials: true });
      await fetchSaved();
    } catch { toast.error('Kunde inte ta bort'); }
  };

  return (
    <div className="space-y-2" data-testid="day-doodle-pad">
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          onClick={() => setTool('pen')}
          className={`px-2 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 ${tool === 'pen' ? 'bg-violet-600 text-white' : 'bg-white border border-violet-200 text-violet-700'}`}
          data-testid="doodle-tool-pen"
        >
          <Paintbrush className="w-3 h-3" /> Penna
        </button>
        <button
          onClick={() => setTool('eraser')}
          className={`px-2 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 ${tool === 'eraser' ? 'bg-violet-600 text-white' : 'bg-white border border-violet-200 text-violet-700'}`}
          data-testid="doodle-tool-eraser"
        >
          <Eraser className="w-3 h-3" /> Sudd
        </button>
        <div className="flex items-center gap-1 px-1.5 py-1 rounded-lg bg-white border border-violet-200">
          {BRUSH_SIZES.map((s) => (
            <button
              key={s}
              onClick={() => setBrushSize(s)}
              className={`rounded-full transition ${brushSize === s ? 'ring-2 ring-violet-500' : ''}`}
              style={{ width: s + 8, height: s + 8, backgroundColor: '#4A2D8E' }}
              data-testid={`doodle-brush-${s}`}
              aria-label={`Penseltjocklek ${s}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-0.5 flex-wrap">
          {COLORS.map((c) => (
            <button
              key={c}
              onClick={() => { setColor(c); setTool('pen'); }}
              className={`w-5 h-5 rounded-full border-2 transition ${color === c ? 'border-violet-700 scale-110' : 'border-white'}`}
              style={{ backgroundColor: c }}
              data-testid={`doodle-color-${c.replace('#', '')}`}
              aria-label={`Färg ${c}`}
            />
          ))}
        </div>
        <button onClick={undo} className="px-2 py-1 rounded-lg text-[10px] font-bold bg-white border border-violet-200 text-violet-700 flex items-center gap-1" data-testid="doodle-undo">
          <Undo2 className="w-3 h-3" /> Ångra
        </button>
        <button onClick={clear} className="px-2 py-1 rounded-lg text-[10px] font-bold bg-white border border-violet-200 text-violet-700 flex items-center gap-1" data-testid="doodle-clear">
          <Sparkle className="w-3 h-3" /> Rensa
        </button>
        <button onClick={save} disabled={busy} className="ml-auto px-3 py-1 rounded-lg text-[10px] font-extrabold bg-violet-600 hover:bg-violet-700 text-white flex items-center gap-1 disabled:opacity-50" data-testid="doodle-save">
          <Save className="w-3 h-3" /> Spara
        </button>
      </div>

      <div ref={containerRef} className="rounded-xl overflow-hidden border-2 border-violet-200 shadow-inner">
        <canvas
          ref={canvasRef}
          className="block touch-none w-full"
          style={{ cursor: tool === 'eraser' ? 'cell' : 'crosshair', height: 220 }}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid="doodle-canvas"
        />
      </div>

      {saved.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] font-extrabold uppercase tracking-widest text-violet-700/70">Sparade doodles · {saved.length}</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
            {saved.map((d) => (
              <div key={d.id} className="relative group rounded-lg overflow-hidden border border-violet-200 bg-white" data-testid={`doodle-thumb-${d.id}`}>
                <img src={d.data_url} alt="doodle" className="w-full h-16 object-cover" />
                <button
                  onClick={() => remove(d.id)}
                  className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-white/90 hover:bg-rose-100 text-rose-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                  title="Ta bort"
                  data-testid={`doodle-delete-${d.id}`}
                >
                  <Trash2 className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
